import { Documentation } from "./Documentation";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { useTranslations } from "next-intl";
import { useMergedGuestTimeline } from "../hooks/useMergedGuestTimeline";
import { PanelEmptyState } from "./PanelEmptyState";

type DocumentationTimelineProps = {
  guestId: string;
};

export const DocumentationTimeline: React.FC<DocumentationTimelineProps> = ({
  guestId,
}) => {
  // The hook owns the fetches (host + companions) and, deliberately, its own
  // loading/error derivation — see useMergedGuestTimeline for why the
  // provider's global flags cannot be used once several timelines load at once.
  const { entries, loading, errorMessage, incompleteCompanions } =
    useMergedGuestTimeline(guestId);
  const t = useTranslations("guest-details");

  return (
    // The scroller stays HERE (min-h-0 + overflow): a long history scrolls
    // inside itself and the note entry under it stays pinned. `flex-1` hands
    // the empty state the room above that entry to centre in.
    <div className="relative mt-3 flex min-h-0 flex-1 flex-col overflow-y-auto">
      {loading ? (
        <LoadingSpinner />
      ) : errorMessage ? (
        <div className="p-4 text-center font-error">{errorMessage}</div>
      ) : (
        <>
          {/* An audit trail that quietly omits rows is worse than one that says
              it is incomplete — and it has to say so on the EMPTY branch too,
              where "no history" would otherwise be an outright lie. */}
          {incompleteCompanions > 0 && (
            <div className="p-2 text-center font-error body-4-regular">
              {t("companionsIncomplete", { count: incompleteCompanions })}
            </div>
          )}
          {entries.length === 0 && (
            <PanelEmptyState riv="NoHistory" title={t("noTimelineAvailable")} />
          )}
          {entries.map((entry, index) => (
            <Documentation
              key={entry.guestLogId}
              guest={entry}
              isLast={index === entries.length - 1}
            />
          ))}
        </>
      )}
    </div>
  );
};
